import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import orderService from '../services/orderService'
import { joinRestaurantOrders } from '../services/orderWebSocket'
import { RestaurantOrder } from '../types/order'
import { useTranslation } from '../hooks/use-i18n'

const KitchenDisplay: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams<{ restaurantId: string }>()
  const [orders, setOrders] = useState<RestaurantOrder[]>([])

  const load = async () => {
    if (!restaurantId) return
    const [submitted, open] = await Promise.all([
      orderService.listOrders(restaurantId, 'submitted'),
      orderService.listOrders(restaurantId, 'open'),
    ])
    setOrders([...submitted, ...open])
  }

  useEffect(() => {
    load()
    if (!restaurantId) return
    return joinRestaurantOrders(restaurantId, () => load())
  }, [restaurantId])

  const columns = [
    { key: 'open', label: t('order.status.open'), filter: (o: RestaurantOrder) => o.status === 'open' },
    { key: 'submitted', label: t('order.status.submitted'), filter: (o: RestaurantOrder) => o.status === 'submitted' },
  ] as const

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{t('kds.title')}</h1>
        <Link to={`/restaurant/${restaurantId}/orders`} className="text-sky-400 text-sm">
          {t('kds.backToOrders')}
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {columns.map((col) => (
          <section key={col.key} className="bg-[#1a1a1a] rounded-2xl p-4 border border-white/10">
            <h2 className="text-xl font-bold mb-4">{col.label}</h2>
            <div className="space-y-3">
              {orders.filter(col.filter).map((order) => (
                <div key={order.id} className="bg-[#2a2a2a] rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between mb-2">
                    <span className="font-bold text-lg">{order.tableLabel || t('order.list.order')}</span>
                    <span className="text-white/60 text-sm">
                      {order.itemCount} {order.itemCount === 1 ? t('common.item') : t('common.items')}
                    </span>
                  </div>
                  <ul className="text-sm text-white/80 space-y-1">
                    {order.items?.map((item) => (
                      <li key={item.id}>
                        {item.quantity}× {item.name}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {orders.filter(col.filter).length === 0 && (
                <p className="text-white/50 text-sm">{t('kds.emptyColumn')}</p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

export default KitchenDisplay
